import Vue from 'vue';
import { client } from '@/services';
import { getSelectedVenue } from '@/services/helpers';
import { PLAYLIST_STATUS } from '@/constants';
import { generateCopyName } from '../../services/helpers';
import apiClient from '@/services/apiClient';

const schedulesService = client.service('schedules');
const playlistsService = client.service('playlists');
const assetsService = client.service('assets');
const daySorter = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

const schedules = {
  namespaced: true,
  state: {
    schedules: [],
    loaded: false,
    paginatedSchedules: [],
    paginatedTotal: 0
  },
  getters: {
    GET_SCHEDULES: state => {
      return state.schedules.map(item => {
        if (item.days.length === 7) {
          item.fullSchedule = `Every day, ${item.startTime} to ${item.endTime}`;
        } else {
          const days = item.days.sort((a, b) => daySorter[a] - daySorter[b]);
          item.fullSchedule = `${days.join(',')}, ${item.startTime} to ${item.endTime}`;
        }

        if (item.priority && item.priority.length && !isNaN(item.priority)) {
          item.priority = parseInt(item.priority);
        }

        return item;
      });
    },
    GET_LOADED_STATUS: state => state.loaded,
    GET_PAGINATED_SCHEDULES: state => state.paginatedSchedules,
    GET_PAGINATED_TOTAL: state => state.paginatedTotal
  },
  mutations: {
    SET_SCHEDULES(state, schedules) {
      state.schedules = schedules;
      state.loaded = true;
    },
    ADD_SCHEDULE(state, schedule) {
      state.schedules.push(schedule);
    },
    UPDATE_SCHEDULE(state, updatedSchedule) {
      const index = state.schedules.findIndex(schedule => schedule._id === updatedSchedule._id);

      if (index >= 0) {
        Vue.set(state.schedules, index, updatedSchedule);
      }
    },
    REMOVE_SCHEDULE(state, _id) {
      const i = state.schedules.findIndex(sched => sched._id === _id);
      if (i >= 0) {
        state.schedules.splice(i, 1);
      }
    },
    SET_PAGINATED_SCHEDULES(state, { data, total }) {
      state.paginatedSchedules = data;
      state.paginatedTotal = total;
    },
    CLEAR_PAGINATED_SCHEDULES(state) {
      state.paginatedSchedules = [];
      state.paginatedTotal = 0;
    }
  },
  actions: {
    // NEW: paginated fetch via REST API
    async FETCH_SCHEDULES_PAGINATED(
      { commit },
      {
        pageIndex = 1,
        pageSize = 10,
        search = '',
        groupName = '',
        sortBy = 'created_at',
        descending = true
      } = {}
    ) {
      const sortFieldMap = {
        sourceType: 'source_type',
        sourceTypeValue: 'source_type_value',
        startDate: 'start_date',
        endDate: 'end_date',
        startTime: 'start_time',
        endTime: 'end_time',
        fullSchedule: 'full_schedule',
        userId: 'user_id',
        venueId: 'venue_id',
        appId: 'app_id',
        updatedAt: 'updated_at',
        createdAt: 'created_at',
        'group.name': 'group_data'
      };

      const dbSortBy = sortFieldMap[sortBy] || sortBy;

      const $skip = (pageIndex - 1) * pageSize;
      const params = {
        $skip,
        $limit: pageSize,
        venueId: getSelectedVenue()._id,
        $with_playlist_and_group: 'true'
      };

      params[`$sort[${dbSortBy}]`] = descending ? -1 : 1;

      if (search) params.$search = search;
      if (groupName) params.group = groupName;

      const { data } = await apiClient.get('/schedules', { params });

      const appIds = [
        ...new Set(
          (data.data || []).filter(s => s.sourceType === 'App' && s.appId).map(s => s.appId)
        )
      ];

      let appMap = {};
      if (appIds.length > 0) {
        const results = await Promise.all(
          appIds.map(id =>
            apiClient
              .get(`/assets/${id}`)
              .then(res => ({ id, data: res.data, success: true }))
              .catch(() => ({ id, success: false }))
          )
        );

        results.forEach(result => {
          if (result.success) {
            appMap[result.id] = result.data;
          }
        });
      }

      const schedules = (data.data || []).map(s => {
        if (!s.sourceType) s.sourceType = 'Playlist';

        switch (s.sourceType) {
          case 'Playlist':
            s.sourceTypeValue = s.playlist && typeof s.playlist === 'object' ? s.playlist.name : '';
            break;
          case 'AdBox Video':
            s.sourceTypeValue = s.assetId || '';
            break;
          case 'IPTV':
          case 'Website':
            s.sourceTypeValue = s.url || '';
            break;
          case 'App':
            s.sourceTypeValue = appMap[s.appId] ? appMap[s.appId].name : 'App deleted.';
            break;
          default:
            s.sourceTypeValue = '';
        }

        if (s.days && s.days.length === 7) {
          s.fullSchedule = `Every day, ${s.startTime} to ${s.endTime}`;
        } else if (s.days && s.days.length > 0) {
          const daySorter = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
          const sorted = [...s.days].sort((a, b) => daySorter[a] - daySorter[b]);
          s.fullSchedule = `${sorted.join(',')}, ${s.startTime} to ${s.endTime}`;
        } else {
          s.fullSchedule = '';
        }

        return s;
      });

      commit('SET_PAGINATED_SCHEDULES', { data: schedules, total: data.total });
      return { data: schedules, total: data.total };
    },
    async GET_SCHEDULES({ commit }) {
      const schedules = await schedulesService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: { value: false },
          $with_playlist_and_group: { value: true }
        }
      });

      const appIds = schedules.filter(s => s.sourceType === 'App' && s.appId).map(s => s.appId);

      let appMap = {};
      if (appIds.length > 0) {
        try {
          const apps = await assetsService.find({
            query: { _id: { $in: appIds }, $limit: appIds.length }
          });
          const appData = apps.data || apps;
          appData.forEach(a => {
            appMap[a._id] = a;
          });
        } catch (e) {}
      }

      schedules.forEach(schedule => {
        if (!schedule.hasOwnProperty('sourceType')) {
          schedule.sourceType = 'Playlist';
        }

        switch (schedule.sourceType) {
          case 'Playlist':
            schedule.sourceTypeValue =
              schedule.playlist && schedule.playlist.name ? schedule.playlist.name : '';
            break;
          case 'AdBox Video':
            schedule.sourceTypeValue = schedule.assetId || '';
            break;
          case 'IPTV':
          case 'Website':
            schedule.sourceTypeValue = schedule.url || '';
            break;
          case 'App':
            schedule.sourceTypeValue = appMap[schedule.appId]
              ? appMap[schedule.appId].name
              : 'App deleted.';
            break;
          default:
            schedule.sourceTypeValue = '';
        }
      });

      commit('SET_SCHEDULES', schedules);
      return schedules;
    },
    async GET_SCHEDULES_BY_NAME(_, scheduleName) {
      const schedules = await schedulesService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: {
            value: false
          },
          $with_playlist_and_group: {
            value: true
          }
        }
      });

      return schedules
        .filter(schedule => schedule.name.toLowerCase().includes(scheduleName.toLowerCase()))
        .map(playlist => playlist.name);
    },
    ADD_SCHEDULE({ rootState }, payload) {
      const schedule = JSON.parse(JSON.stringify(payload));

      if (schedule.priority && schedule.priority.length && !isNaN(schedule.priority)) {
        schedule.priority = parseInt(schedule.priority);
      }

      schedule.status = PLAYLIST_STATUS.DRAFT;
      schedule.created_at = Date.now();
      schedule.userId = rootState.users.user._id;
      schedule.venueId = getSelectedVenue()._id;
      return schedulesService.create(schedule);
    },
    async ADD_SCHEDULE_DUPLICATE({ rootState, dispatch }, payload) {
      try {
        const sameNameList = await dispatch('GET_SCHEDULES_BY_NAME', payload.name);

        delete payload._id;
        payload.name = generateCopyName(payload.name, sameNameList);

        const clonedSchedule = await schedulesService.create(payload);
        return clonedSchedule;
      } catch (error) {
        throw error;
      }
    },

    async UPDATE_SCHEDULE({ commit }, payload) {
      try {
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        if (schedule.priority && schedule.priority.length && !isNaN(schedule.priority)) {
          schedule.priority = parseInt(schedule.priority);
        }

        schedule.venueId = getSelectedVenue()._id;
        schedule.updated_at = Date.now();
        schedule.status = payload.status;

        const updated = await schedulesService.update(schedule._id, schedule);
        commit('UPDATE_SCHEDULE', updated);
        return updated;
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_SCHEDULE_STATUS({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.status = payload.status;

        const patched = await schedulesService.patch(schedule._id, schedule);

        commit('UPDATE_SCHEDULE', patched);
        return patched;
      } catch (error) {
        throw error;
      }
    },
    UPDATE_SCHEDULE_TAKEOVER({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.takeover = payload.takeover;

        commit('UPDATE_SCHEDULE', schedule);

        return schedulesService.patch(schedule._id, {
          takeover: payload.takeover,
          updated_at: schedule.updated_at
        });
      } catch (error) {
        throw error;
      }
    },
    UPDATE_SCHEDULE_TAKEOVER({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.takeover = payload.takeover;

        commit('UPDATE_SCHEDULE', schedule);

        return schedulesService.patch(schedule._id, {
          takeover: payload.takeover,
          updated_at: schedule.updated_at
        });
      } catch (error) {
        throw error;
      }
    },

    async DELETE_SCHEDULES({ commit }, schedules) {
      for (const schedule of schedules) {
        await schedulesService.remove(schedule._id);
        commit('REMOVE_SCHEDULE', schedule._id);
      }
      return { success: true };
    },
    async GET_ONE_SCHEDULE(_, id) {
      const response = await schedulesService.get(id);

      try {
        response.startDate = response.startDate;
        response.endDate = response.endDate;
        response.playlist = await playlistsService.get(response.playlist);
      } catch (e) {
        response.playlist = null;
      }

      return response;
    },
    async SEARCH_PLAYLIST(_, _id) {
      try {
        return (
          await schedulesService.find({
            query: {
              playlist: _id
            }
          })
        ).data;
      } catch (e) {
        throw e;
      }
    },
    async SEARCH_DEVICE_GROUP(_, _id) {
      try {
        return (
          await schedulesService.find({
            query: {
              group: _id
            }
          })
        ).data;
      } catch (e) {
        throw e;
      }
    },
    async SEARCH_APP_ASSET(_, _id) {
      return (
        await schedulesService.find({
          query: {
            appId: _id
          }
        })
      ).data;
    }
  }
};

export default schedules;



// WEBPACK FOOTER //
// ./src/store/modules/schedules.js